import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  DashboardSummary,
  type DashboardSummary as DashboardSummaryType,
  NoShowResponse,
  type NoShowResponse as NoShowResponseType,
  RecentScansResponse,
  type RecentScansResponse as RecentScansResponseType,
  SendEmailResponse,
} from '@tunodo/ticketing-shared';
import {
  ScanLine,
  Settings,
  TrendingUp,
  UserCheck,
  UserCog,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_app/')({
  component: HomePage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH} h`;
  return new Date(iso).toLocaleDateString('es-ES');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetricCards({ summary }: { summary: DashboardSummaryType | undefined }) {
  const pct =
    summary && summary.totalAttendees > 0
      ? `${Math.round((summary.attendedCount / summary.totalAttendees) * 100)}%`
      : '—';

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Total inscritos */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Total inscritos</CardTitle>
        </CardHeader>
        <CardContent>
          {summary === undefined ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-3xl font-bold">{summary.totalAttendees}</p>
          )}
        </CardContent>
      </Card>

      {/* Asistieron */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
          <UserCheck className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Asistieron</CardTitle>
        </CardHeader>
        <CardContent>
          {summary === undefined ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <>
              <p className="text-3xl font-bold">{summary.attendedCount}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {summary.pendingAttendance} pendientes
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Almuerzos */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
          <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Almuerzos</CardTitle>
        </CardHeader>
        <CardContent>
          {summary === undefined ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <>
              <p className="text-3xl font-bold">{summary.lunchClaimedCount}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {summary.pendingLunch} pendientes
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* % Asistencia */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">% Asistencia</CardTitle>
        </CardHeader>
        <CardContent>
          {summary === undefined ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-3xl font-bold">{pct}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RecentScansList({ data }: { data: RecentScansResponseType | undefined }) {
  if (data === undefined) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
          <div key={i} className="flex items-center gap-3 rounded-md border px-4 py-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-5 w-16 ml-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (data.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        Aún no hay escaneos registrados.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {data.items.map((scan) => {
        const attendeeName =
          scan.attendee?.fullName ??
          `Token: ${scan.attemptedToken ? `${scan.attemptedToken.slice(0, 8)}…` : '?'}`;

        return (
          <div
            key={scan.id}
            className="flex flex-wrap items-center gap-2 rounded-md border px-4 py-2.5"
          >
            <span className="font-medium text-sm flex-1 min-w-0 truncate">{attendeeName}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <Badge
                variant="outline"
                className={
                  scan.type === 'ATTENDANCE'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-orange-500 text-orange-600'
                }
              >
                {scan.type === 'ATTENDANCE' ? 'Asistencia' : 'Almuerzo'}
              </Badge>
              <Badge
                variant={
                  scan.result === 'OK'
                    ? 'default'
                    : scan.result === 'DUPLICATE'
                      ? 'secondary'
                      : 'destructive'
                }
                className={
                  scan.result === 'OK'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : scan.result === 'DUPLICATE'
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-400'
                      : undefined
                }
              >
                {scan.result === 'OK'
                  ? 'OK'
                  : scan.result === 'DUPLICATE'
                    ? 'Duplicado'
                    : scan.result === 'REVOKED'
                      ? 'Revocado'
                      : 'Inválido'}
              </Badge>
            </div>
            <div className="w-full flex justify-between text-xs text-muted-foreground mt-0.5">
              <span>{scan.by.fullName}</span>
              <span>{relativeTime(scan.scannedAt)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NoShowTable({ data }: { data: NoShowResponseType | undefined }) {
  const queryClient = useQueryClient();

  const sendEmailMutation = useMutation({
    mutationFn: (attendeeId: string) =>
      apiFetch(`/api/attendees/${attendeeId}/send-email`, {
        method: 'POST',
        schema: SendEmailResponse,
      }),
    onSuccess: (data) => {
      toast.success(data.messageId ? `Email reenviado (${data.messageId})` : 'Email reenviado');
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'no-show'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Error al reenviar email');
    },
  });

  if (data === undefined) {
    return (
      <div className="rounded-md border overflow-x-auto">
        <Table className="min-w-[560px]">
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 4 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-40" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-48" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-8 w-28" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (data.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        Todos los inscritos ya asistieron.
      </p>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table className="min-w-[560px]">
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead className="w-32" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((attendee) => (
            <TableRow key={attendee.id}>
              <TableCell className="font-medium">{attendee.fullName}</TableCell>
              <TableCell className="text-muted-foreground">{attendee.email}</TableCell>
              <TableCell className="text-muted-foreground">{attendee.phone}</TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sendEmailMutation.isPending}
                  onClick={() => void sendEmailMutation.mutate(attendee.id)}
                >
                  Reenviar email
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function HomePage() {
  const user = useAuthStore((s) => s.user);

  // Non-organizer staff: action cards
  if (user?.role !== 'ORGANIZER') {
    const isAttendance = user?.role === 'STAFF_ATTENDANCE';
    const isLunch = user?.role === 'STAFF_LUNCH';
    const isScanner = user?.role === 'STAFF_SCANNER';

    const showAttendance = isAttendance || isScanner;
    const showLunch = isLunch || isScanner;

    return (
      <main className="flex-1 p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Bienvenido, {user?.fullName ?? 'Staff'}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Selecciona tu punto de escaneo</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {showAttendance && (
            <Link
              to="/staff/scan/attendance"
              className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-primary/30 bg-primary/5 p-8 text-center transition-colors hover:bg-primary/10 active:bg-primary/20"
            >
              <ScanLine className="h-12 w-12 text-primary" />
              <div>
                <p className="font-semibold text-lg">Escanear Entrada</p>
                <p className="text-sm text-muted-foreground mt-1">Marcar asistencia</p>
              </div>
            </Link>
          )}
          {showLunch && (
            <Link
              to="/staff/scan/lunch"
              className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-orange-400/40 bg-orange-50 p-8 text-center transition-colors hover:bg-orange-100 active:bg-orange-200"
            >
              <UtensilsCrossed className="h-12 w-12 text-orange-500" />
              <div>
                <p className="font-semibold text-lg">Escanear Almuerzo</p>
                <p className="text-sm text-muted-foreground mt-1">Registrar almuerzo</p>
              </div>
            </Link>
          )}
        </div>
      </main>
    );
  }

  return <OrganizerDashboard />;
}

const ticketingEventSchema = z.array(
  z.object({ id: z.string(), name: z.string(), date: z.string() }),
);

function OrganizerDashboard() {
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  const { data: ticketingEvents } = useQuery({
    queryKey: ['ticketing-events'],
    queryFn: () =>
      apiFetch('/api/passport-admin/ticketing-events', { schema: ticketingEventSchema }),
    staleTime: 60_000,
  });

  // Auto-seleccionar el primer evento cuando carguen
  const eventId = selectedEventId || ticketingEvents?.[0]?.id;
  const qs = (base: string) => (eventId ? `${base}?eventId=${eventId}` : base);

  const { data: summary } = useQuery<DashboardSummaryType>({
    queryKey: ['dashboard', 'summary', eventId],
    queryFn: () => apiFetch(qs('/api/dashboard/summary'), { schema: DashboardSummary }),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    enabled: !!eventId,
  });

  const { data: recentScans } = useQuery<RecentScansResponseType>({
    queryKey: ['dashboard', 'recent-scans', eventId],
    queryFn: () =>
      apiFetch(
        eventId
          ? `/api/dashboard/recent-scans?limit=20&eventId=${eventId}`
          : '/api/dashboard/recent-scans?limit=20',
        { schema: RecentScansResponse },
      ),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    enabled: !!eventId,
  });

  const { data: noShow } = useQuery<NoShowResponseType>({
    queryKey: ['dashboard', 'no-show', eventId],
    queryFn: () => apiFetch(qs('/api/dashboard/no-show'), { schema: NoShowResponse }),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    enabled: !!eventId,
  });

  const showEventSelector = ticketingEvents && ticketingEvents.length > 1;

  return (
    <main className="flex-1 p-6 space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Métricas del evento · actualización automática cada 15 s
          </p>
        </div>
        {showEventSelector && (
          <Select
            value={selectedEventId || ticketingEvents[0]?.id}
            onValueChange={setSelectedEventId}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Selecciona evento" />
            </SelectTrigger>
            <SelectContent>
              {ticketingEvents.map((ev) => (
                <SelectItem key={ev.id} value={ev.id}>
                  {ev.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Sección admin — accesos rápidos */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/admin/event-settings"
          className="flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium transition-colors hover:bg-accent"
        >
          <Settings className="h-4 w-4 text-muted-foreground shrink-0" />
          Configuración del evento
        </Link>
        <Link
          to="/admin/staff"
          className="flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium transition-colors hover:bg-accent"
        >
          <UserCog className="h-4 w-4 text-muted-foreground shrink-0" />
          Gestión de staff
        </Link>
      </div>

      {/* Sección 1 — Cards de métricas */}
      <MetricCards summary={summary} />

      {/* Sección 2 — Últimos escaneos */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold">
          Últimos escaneos
          {recentScans && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({recentScans.total} total)
            </span>
          )}
        </h2>
        <RecentScansList data={recentScans} />
      </section>

      {/* Sección 3 — No-shows */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold">
          No asistieron
          {noShow && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">({noShow.total})</span>
          )}
        </h2>
        <NoShowTable data={noShow} />
      </section>
    </main>
  );
}
